import React, { useState, useEffect, useRef, useCallback } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import Sidebar from "./Sidebar";
import Header from "../components/Header";
import { useAuthState } from "react-firebase-hooks/auth";
import { getDatabase, ref, onValue, get, remove, update } from "firebase/database";
import {
  getAuth,
  deleteUser,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword,
  signOut,
} from "firebase/auth";
import { app } from "../firebase";
import { useNavigate } from "react-router-dom";
import Logout from "@mui/icons-material/Logout";
import Delete from "@mui/icons-material/Delete";
import axios from "axios";
import { FirebaseError } from "firebase/app";
import { trackClick } from "../utils/analytics";
import { deleteAccount, deleteAccountData } from "../utils/accountDeletion";
import { fetchMyStreams } from "../utils/streams";
import { requestEmailChange } from "../utils/emailChange";
import { eventDateProblem, phoneProblem, todayLocal } from "../utils/eventDetails";
import {
  User,
  Mail,
  Lock,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  Trash2,
  LogOut,
  Settings,
  Key
} from "lucide-react";

const AccountDetails: React.FC = () => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableElement = useRef<HTMLDivElement>(null);
  const lastFocusableElement = useRef<HTMLDivElement>(null);

  const [userData, setUserData] = useState({ name: "", email: "" });
  const [phone, setPhone] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [savedDetails, setSavedDetails] = useState({ phone: "", eventDate: "" });
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsStatus, setDetailsStatus] = useState<{ type: "success" | "error" | null; message: string }>({ type: null, message: "" });
  const [user, loading, error] = useAuthState(getAuth(app));
  const [showPasswordConfirmationModal, setShowPasswordConfirmationModal] =
    useState(false);
  const [password, setPassword] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [emailUpdateStatus, setEmailUpdateStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });
  const [showPasswords, setShowPasswords] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [passwordUpdateStatus, setPasswordUpdateStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });

  const navigate = useNavigate();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const passwordRequirements = {
    length: newPassword.length >= 6,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
    match: newPassword === confirmPassword && newPassword !== "",
  };

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);

  const trimmedNewEmail = newEmail.trim();
  const isNewEmailValid =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedNewEmail) &&
    trimmedNewEmail.toLowerCase() !== (user?.email ?? "").toLowerCase();

  // Both changes are sensitive operations, so the current password is always re-checked first.
  const reauthenticate = (currentUser: NonNullable<typeof user>, pw: string) =>
    reauthenticateWithCredential(
      currentUser,
      EmailAuthProvider.credential(currentUser.email || "", pw)
    );

  const isWrongPassword = (error: unknown) =>
    error instanceof FirebaseError &&
    (error.code === "auth/wrong-password" || error.code === "auth/invalid-credential");

  const handleUpdateEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isNewEmailValid || !emailPassword || !user) return;

    setIsUpdatingEmail(true);
    setEmailUpdateStatus({ type: null, message: "" });

    try {
      await reauthenticate(user, emailPassword);
      // The API sends the confirmation to the new address and a notice to this one; it needs a login token from the password check just made.
      const result = await requestEmailChange(() => user.getIdToken(true), trimmedNewEmail);
      if (result.status !== "sent") {
        const messages = {
          "in-use": "That email address is already used by another account.",
          reauthenticate: "Please enter your password again to change your email.",
          wait: result.status === "wait" ? `Please wait ${result.retryAfterSeconds} seconds before trying again.` : "",
          invalid: result.status === "invalid" ? result.message : "",
          error: "Failed to start the email change. Please try again.",
        };
        setEmailUpdateStatus({ type: "error", message: messages[result.status] });
        trackClick("error-update_email");
        return;
      }
      setEmailUpdateStatus({
        type: "success",
        message: `We sent a confirmation link to ${trimmedNewEmail} and a notice to ${user.email}. Your email changes once you open the link; until then you keep signing in with ${user.email}.`,
      });
      setNewEmail("");
      setEmailPassword("");
      trackClick("success-update_email");
    } catch (error) {
      console.error("Error updating email:", error);
      const code = error instanceof FirebaseError ? error.code : "";
      let message = "Failed to start the email change. Please try again.";
      if (isWrongPassword(error)) message = "Your current password is incorrect.";
      else if (code === "auth/invalid-email") message = "Please enter a valid email address.";
      else if (code === "auth/too-many-requests") message = "Too many attempts. Please wait a moment and try again.";
      setEmailUpdateStatus({ type: "error", message });
      trackClick("error-update_email");
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isPasswordValid || !currentPassword || !user) return;

    setIsUpdatingPassword(true);
    setPasswordUpdateStatus({ type: null, message: "" });

    try {
      await reauthenticate(user, currentPassword);
      await updatePassword(user, newPassword);
      setPasswordUpdateStatus({
        type: "success",
        message: "Password updated successfully! Your other devices will be signed out.",
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      trackClick("success-update_password");
    } catch (error) {
      console.error("Error updating password:", error);
      if (isWrongPassword(error)) {
        setPasswordUpdateStatus({
          type: "error",
          message: "Your current password is incorrect.",
        });
      } else if (error instanceof FirebaseError && error.code === "auth/requires-recent-login") {
        setPasswordUpdateStatus({
          type: "error",
          message: "Please log out and log back in to update your password for security reasons.",
        });
      } else {
        setPasswordUpdateStatus({
          type: "error",
          message: "Failed to update password. Please try again.",
        });
      }
      trackClick("error-update_password");
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const cancelDelete = () => {
    setShowConfirmationModal(false);
  };
  // An event date already saved is kept even once it has passed; only a changed date has to be today or later.
  const phoneError = phoneProblem(phone);
  const dateError = eventDate === savedDetails.eventDate ? null : eventDateProblem(eventDate);
  const detailsChanged = phone.trim() !== savedDetails.phone || eventDate !== savedDetails.eventDate;

  const handleSaveDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || phoneError || dateError || !detailsChanged) return;
    setSavingDetails(true);
    setDetailsStatus({ type: null, message: "" });
    try {
      // null removes the field, so clearing a box really clears it.
      await update(ref(getDatabase(app), `users/${user.uid}`), { phone: phone.trim() || null, eventDate: eventDate || null });
      setSavedDetails({ phone: phone.trim(), eventDate });
      setPhone(phone.trim());
      setDetailsStatus({ type: "success", message: "Saved." });
      trackClick("success-update_event_details");
    } catch (error) {
      console.error("Error saving contact details:", error);
      setDetailsStatus({ type: "error", message: "We couldn't save that. Please try again." });
    } finally {
      setSavingDetails(false);
    }
  };

  const cancelLogout = useCallback(() => {
    setShowLogoutModal(false);
  }, []);

  useEffect(() => {
    if (user) {
      const db = getDatabase(app);
      const userRef = ref(db, `users/${user.uid}`);
      onValue(
        userRef,
        (snapshot) => {
          const data = snapshot.val();
          setUserData({
            name: user.displayName || "N/A",
            email: user.email || "N/A",
          });
          const loaded = { phone: data?.phone ?? "", eventDate: data?.eventDate ?? "" };
          setPhone(loaded.phone);
          setEventDate(loaded.eventDate);
          setSavedDetails(loaded);
        },
        { onlyOnce: true }
      );
    }
  }, [user]);

  useEffect(() => {
    if (showLogoutModal) {
      modalRef.current?.focus();

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          cancelLogout();
        } else if (event.key === "Tab") {
          if (event.shiftKey) {
            if (document.activeElement === firstFocusableElement.current) {
              event.preventDefault();
              lastFocusableElement.current?.focus();
            }
          } else {
            if (document.activeElement === lastFocusableElement.current) {
              event.preventDefault();
              firstFocusableElement.current?.focus();
            }
          }
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [showLogoutModal, cancelLogout]);

  const handleReauthenticateAndDelete = async (password: string) => {
    if (!user || !user.email) return;

    const credential = EmailAuthProvider.credential(user.email, password);
    try {
      await reauthenticateWithCredential(user, credential);
      const db = getDatabase(app);
      const userRef = ref(db, `users/${user.uid}`);
      await deleteAccount({
        readStreamNames: async () => {
          const names: string[] = [];
          const profileName = (await get(userRef)).val()?.name;
          if (profileName) names.push(profileName);
          try { names.push(...(await fetchMyStreams(() => user.getIdToken())).map((s) => s.name)); }
          catch (error) { console.error("Could not list the account's streams; deleting the profile's stream only.", error); }
          return names;
        },
        // The login authenticates this call, so it has to run before the login is deleted. A fresh token: the re-authentication above just ran.
        deleteData: () => deleteAccountData(() => user.getIdToken(true)),
        deleteStream: (name) => DeleteStream(name),
        removeProfile: () => remove(userRef),
        deleteLogin: () => deleteUser(user),
      });
      navigate("/Signup");
      trackClick("success-delete_account");
    } catch (error) {
      if (error instanceof FirebaseError) {
        console.error(error.message);
      }
      console.error(
        "Error during re-authentication or account deletion:",
        error
      );
      alert(
        "Error during re-authentication or account deletion. Please check the console for more details."
      );
    }
  };

  const DeleteStream = async (name: string) => {
    const apiUrl = "https://api.deafassistant.com/stream/DeleteStream";
    const data = {
      oldName: name.toLowerCase(),
      uid: user?.uid
    };

    try {
      axios.defaults.headers.post["Accept"] = "application/json; charset=utf-8";
      await axios.post(apiUrl, data);
    } catch (error) {
      console.error("There was an error!", error);
    }
  };

  const handleDeleteAccount = () => {
    trackClick("button_click-delete_account");
    setShowConfirmationModal(true);
  };

  const toggleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmDelete = () => {
    setShowConfirmationModal(false);
    setShowPasswordConfirmationModal(true);
  };
  const submitPasswordAndDelete = async (password: string) => {
    setShowPasswordConfirmationModal(false);
    handleReauthenticateAndDelete(password);
  };
  const handleLogout = async () => {
    trackClick("button_click-logout");
    const auth = getAuth();

    try {
      await signOut(auth);
      navigate("/login");
      trackClick("success-logout");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <div className="flex flex-col h-screen bg-slate-50 font-inter overflow-hidden">
      <Header onDeleteAccount={handleDeleteAccount} />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar activeItem="profile" />
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">

            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <h1 className="text-4xl font-bold text-slate-900 tracking-tight">
                  {t("account.title")}
                </h1>
                <p className="text-slate-500 mt-1 text-lg">
                  Manage your personal information and security settings.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={toggleLogout}
                  className="btn-secondary flex items-center gap-2 group"
                >
                  <LogOut size={18} className="text-slate-400 group-hover:text-blue-600 transition-colors" />
                  {t("auth.logout")}
                </button>
                <button
                  onClick={handleDeleteAccount}
                  className="btn-danger flex items-center gap-2 group"
                >
                  <Trash2 size={18} className="text-red-400 group-hover:text-red-600 transition-colors" />
                  {t("account.deleteAccount")}
                </button>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8">

              {/* Profile Card */}
              <div className="md:col-span-1 min-w-0 space-y-6">
                <div className="glass-card rounded-3xl p-8 flex flex-col items-center text-center">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-3xl font-bold mb-4 shadow-lg ring-4 ring-white">
                    {userData.name ? userData.name.charAt(0).toUpperCase() : <User size={40} />}
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 max-w-full break-words">{userData.name}</h2>
                  <p className="text-slate-500 text-sm max-w-full break-all">{userData.email}</p>

                  <div className="mt-8 pt-8 border-t border-slate-100 w-full text-left space-y-4">
                    <div className="flex items-center gap-3 text-slate-600">
                      <ShieldCheck size={18} className="text-green-500" />
                      <span className="text-sm font-medium">Account Verified</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-600">
                      <Settings size={18} className="text-blue-500" />
                      <span className="text-sm font-medium">Standard Account</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Details & Security */}
              <div className="md:col-span-2 space-y-8">

                {/* Account Information */}
                <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                      <User size={20} />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">Personal Information</h3>
                  </div>

                  <div className="space-y-6">
                    <div className="group">
                      <label className="block text-sm font-semibold text-slate-700 mb-2 ml-1 transition-colors group-focus-within:text-blue-600">
                        Full Name
                      </label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" size={18} />
                        <input
                          type="text"
                          value={userData.name}
                          readOnly
                          className="w-full bg-slate-50 border border-slate-200 rounded-2xl py-3 pl-12 pr-4 text-slate-600 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all cursor-not-allowed"
                        />
                      </div>
                    </div>

                    <div className="group">
                      <label className="block text-sm font-semibold text-slate-700 mb-2 ml-1 transition-colors group-focus-within:text-blue-600">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" size={18} />
                        <input
                          type="email"
                          value={userData.email}
                          readOnly
                          className="w-full bg-slate-50 border border-slate-200 rounded-2xl py-3 pl-12 pr-4 text-slate-600 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all cursor-not-allowed"
                        />
                      </div>
                    </div>

                    <form onSubmit={handleSaveDetails} className="space-y-6 pt-6 border-t border-slate-100">
                      <div>
                        <label htmlFor="account-phone" className="block text-sm font-semibold text-slate-700 mb-2 ml-1">
                          Phone Number <span className="font-normal text-slate-400">(optional)</span>
                        </label>
                        <input
                          id="account-phone"
                          type="tel"
                          autoComplete="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="+1 555 123 4567"
                          className="w-full bg-white border border-slate-200 rounded-2xl py-3 px-4 text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                        />
                        {phoneError && <p role="alert" className="text-red-600 text-sm mt-1 ml-1">{phoneError}</p>}
                      </div>
                      <div>
                        <label htmlFor="account-event-date" className="block text-sm font-semibold text-slate-700 mb-2 ml-1">
                          Event Date <span className="font-normal text-slate-400">(optional)</span>
                        </label>
                        <input
                          id="account-event-date"
                          type="date"
                          min={todayLocal()}
                          value={eventDate}
                          onChange={(e) => setEventDate(e.target.value)}
                          className="w-full bg-white border border-slate-200 rounded-2xl py-3 px-4 text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                        />
                        {dateError && <p role="alert" className="text-red-600 text-sm mt-1 ml-1">{dateError}</p>}
                      </div>
                      {detailsStatus.message && (
                        <div className={`p-3 rounded-2xl flex items-center gap-2 text-sm font-medium ${detailsStatus.type === "success" ? "bg-green-50 text-green-700 border border-green-100" : "bg-red-50 text-red-700 border border-red-100"}`}>
                          {detailsStatus.type === "success" ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />} {detailsStatus.message}
                        </div>
                      )}
                      <button
                        type="submit"
                        disabled={!detailsChanged || !!phoneError || !!dateError || savingDetails}
                        className="btn-primary w-full disabled:opacity-60"
                      >
                        {savingDetails ? "Saving..." : "Save"}
                      </button>
                    </form>
                  </div>
                </div>

                {/* Email Update */}
                <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                      <Mail size={20} />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">Change Email</h3>
                  </div>

                  <form onSubmit={handleUpdateEmail} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="group">
                        <label className="block text-sm font-semibold text-slate-700 mb-2 ml-1">
                          New Email Address
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" size={18} />
                          <input
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder="new@example.com"
                            autoComplete="email"
                            className="w-full bg-white border border-slate-200 rounded-2xl py-3 pl-12 pr-4 text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                          />
                        </div>
                      </div>

                      <div className="group">
                        <label className="block text-sm font-semibold text-slate-700 mb-2 ml-1">
                          Current Password
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" size={18} />
                          <input
                            type="password"
                            value={emailPassword}
                            onChange={(e) => setEmailPassword(e.target.value)}
                            placeholder="••••••••"
                            autoComplete="current-password"
                            className="w-full bg-white border border-slate-200 rounded-2xl py-3 pl-12 pr-4 text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                          />
                        </div>
                      </div>
                    </div>

                    {emailUpdateStatus.message && (
                      <div className={`p-4 rounded-2xl flex items-start gap-3 ${emailUpdateStatus.type === "success"
                        ? "bg-green-50 text-green-700 border border-green-100"
                        : "bg-red-50 text-red-700 border border-red-100"
                        }`}>
                        {emailUpdateStatus.type === "success" ? <CheckCircle2 size={18} className="mt-0.5 shrink-0" /> : <AlertCircle size={18} className="mt-0.5 shrink-0" />}
                        <p className="text-sm font-medium">{emailUpdateStatus.message}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={!isNewEmailValid || !emailPassword || isUpdatingEmail}
                      className="btn-primary w-full flex items-center justify-center gap-2"
                    >
                      {isUpdatingEmail ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Mail size={20} />
                      )}
                      Send Confirmation Link
                    </button>
                  </form>
                </div>

                {/* Password Update */}
                <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                      <Key size={20} />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">Update Password</h3>
                  </div>

                  <form onSubmit={handleUpdatePassword} className="space-y-6">
                    <div className="group">
                      <label className="block text-sm font-semibold text-slate-700 mb-2 ml-1">
                        Current Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
                        <input
                          type={showPasswords ? "text" : "password"}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          placeholder="••••••••"
                          autoComplete="current-password"
                          className="w-full bg-white border border-slate-200 rounded-2xl py-3 pl-12 pr-4 text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="group">
                        <label className="block text-sm font-semibold text-slate-700 mb-2 ml-1">
                          New Password
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
                          <input
                            type={showPasswords ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="••••••••"
                            className="w-full bg-white border border-slate-200 rounded-2xl py-3 pl-12 pr-12 text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswords(!showPasswords)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                          >
                            {showPasswords ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>

                      <div className="group">
                        <label className="block text-sm font-semibold text-slate-700 mb-2 ml-1">
                          Confirm Password
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
                          <input
                            type={showPasswords ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="••••••••"
                            className="w-full bg-white border border-slate-200 rounded-2xl py-3 pl-12 pr-12 text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Requirements Checklist */}
                    <div className="bg-slate-50 rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Requirement label="At least 6 characters" met={passwordRequirements.length} />
                      <Requirement label="One uppercase letter" met={passwordRequirements.uppercase} />
                      <Requirement label="One lowercase letter" met={passwordRequirements.lowercase} />
                      <Requirement label="One number" met={passwordRequirements.number} />
                      <Requirement label="One special character" met={passwordRequirements.special} />
                      <Requirement label="Passwords match" met={passwordRequirements.match} />
                    </div>

                    {passwordUpdateStatus.message && (
                      <div className={`p-4 rounded-2xl flex items-start gap-3 ${passwordUpdateStatus.type === "success"
                        ? "bg-green-50 text-green-700 border border-green-100"
                        : "bg-red-50 text-red-700 border border-red-100"
                        }`}>
                        {passwordUpdateStatus.type === "success" ? <CheckCircle2 size={18} className="mt-0.5 shrink-0" /> : <AlertCircle size={18} className="mt-0.5 shrink-0" />}
                        <p className="text-sm font-medium">{passwordUpdateStatus.message}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={!isPasswordValid || !currentPassword || isUpdatingPassword}
                      className="btn-primary w-full flex items-center justify-center gap-2"
                    >
                      {isUpdatingPassword ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <ShieldCheck size={20} />
                      )}
                      Update Password
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Modals */}
      <Modal
        isOpen={showLogoutModal}
        onClose={cancelLogout}
        title={t("account.logoutConfirmTitle")}
        onConfirm={handleLogout}
        confirmText={t("common.confirm")}
        cancelText={t("common.cancel")}
        icon={<LogOut className="text-blue-600" size={24} />}
      />

      <Modal
        isOpen={showConfirmationModal}
        onClose={cancelDelete}
        title={t("account.deleteConfirmTitle")}
        onConfirm={confirmDelete}
        confirmText={t("common.confirm")}
        cancelText={t("common.cancel")}
        confirmVariant="danger"
        icon={<Trash2 className="text-red-600" size={24} />}
      />

      <PasswordModal
        isOpen={showPasswordConfirmationModal}
        onClose={() => setShowPasswordConfirmationModal(false)}
        password={password}
        setPassword={setPassword}
        onSubmit={() => submitPasswordAndDelete(password)}
      />
    </div>
  );
};

// Sub-components for cleaner code
const Requirement = ({ label, met }: { label: string; met: boolean }) => (
  <div className="flex items-center gap-2">
    {met ? (
      <CheckCircle2 size={16} className="text-green-500 shrink-0" />
    ) : (
      <div className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0" />
    )}
    <span className={`text-xs font-medium transition-colors ${met ? "text-slate-700" : "text-slate-400"}`}>
      {label}
    </span>
  </div>
);

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  icon?: React.ReactNode;
  confirmText: string;
  cancelText: string;
  confirmVariant?: "primary" | "danger";
}

const Modal = ({ isOpen, onClose, onConfirm, title, icon, confirmText, cancelText, confirmVariant = "primary" }: ModalProps) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-300" onClick={onClose} />
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md p-8 relative animate-in zoom-in-95 fade-in duration-300">
        <button onClick={onClose} className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 transition-colors">
          <X size={20} />
        </button>
        <div className="flex flex-col items-center text-center space-y-4">
          {icon && <div className={`p-4 rounded-2xl ${confirmVariant === "danger" ? "bg-red-50" : "bg-blue-50"}`}>{icon}</div>}
          <h3 className="text-xl font-bold text-slate-900">{title}</h3>
          <div className="flex gap-3 w-full mt-4">
            <button onClick={onClose} className="btn-secondary flex-1">{cancelText}</button>
            <button onClick={onConfirm} className={`flex-1 ${confirmVariant === "danger" ? "bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl" : "btn-primary"}`}>
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PasswordModal = ({ isOpen, onClose, password, setPassword, onSubmit }: any) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-300" onClick={onClose} />
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md p-8 relative animate-in zoom-in-95 fade-in duration-300">
        <button onClick={onClose} className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 transition-colors">
          <X size={20} />
        </button>
        <div className="space-y-6">
          <div className="flex flex-col items-center text-center space-y-2">
            <div className="p-4 bg-red-50 rounded-2xl text-red-600"><Lock size={24} /></div>
            <h3 className="text-xl font-bold text-slate-900">Confirm Deletion</h3>
            <p className="text-slate-500 text-sm">Please enter your password to confirm account deletion.</p>
          </div>
          <input
            type="password"
            className="w-full bg-slate-50 border border-slate-200 rounded-2xl py-3 px-4 text-slate-900 outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
            value={password}
            placeholder="Enter password"
            onChange={(e) => setPassword(e.target.value)}
          />
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button onClick={onSubmit} className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 px-6 rounded-xl transition-all flex-1 shadow-sm">
              Confirm Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountDetails;
